import { floorDisplaySeconds, formatSecondsAsTimecode } from '@kiao-voice/core'
import type { SubtitleCue } from './subtitle-demo-cues'

/**
 * 字幕示範的純函式。抽出來是因為「講者只在換人時標一次」與「時間碼要跟
 * YouTube 的起播秒數對得起來」都是可以單獨驗的規則，混在 JSX 裡只能靠目視。
 */

export interface GroupedCue extends SubtitleCue {
  /** 在原始清單裡的位置。點擊與 `aria-current` 都認這個，不是組內的 index。 */
  index: number
}

export interface SpeakerGroup {
  /** 講者名；逐字稿沒有講者時是 null，畫面就不標人名。 */
  name: string | null
  /**
   * 依**首次出現順序**編號（0 起算）。設計稿是「第二位用墨色、其餘用灰」，而
   * 講者是後端給的名字、數量也不固定，所以配色認出場順序，不認名字。
   */
  order: number
  cues: GroupedCue[]
}

/**
 * 依講者分組——**只在換人時開新組**，不是把同一個人的句子全部收在一起。
 * 對話會來回切換，按人合併會把時間順序打散。
 */
export function groupCuesBySpeaker(cues: SubtitleCue[]): SpeakerGroup[] {
  const groups: SpeakerGroup[] = []
  const orderByName = new Map<string, number>()
  cues.forEach((cue, index) => {
    const last = groups.at(-1)
    if (last && last.name === cue.speaker) {
      last.cues.push({ ...cue, index })
      return
    }
    if (cue.speaker !== null && !orderByName.has(cue.speaker)) {
      orderByName.set(cue.speaker, orderByName.size)
    }
    groups.push({
      name: cue.speaker,
      order: cue.speaker === null ? 0 : (orderByName.get(cue.speaker) ?? 0),
      cues: [{ ...cue, index }],
    })
  })
  return groups
}

/**
 * 字幕示範的時間碼：`00:30`，**分鐘補零**。
 *
 * 不用 `transcript-view-model` 的 `formatTimecode`——那一支是 `m:ss`（
 * `padMinutes: false`），字幕清單裡一整排長短不一的時間欄會抖。兩支底下是
 * 同一個 `formatSecondsAsTimecode`，差別只有補零。
 */
export function formatCueClock(secs: number): string {
  return formatSecondsAsTimecode(secs)
}

/**
 * 播到 `secs` 秒時，逐字稿該高亮哪一句：**最後一個已經開始的句子**。
 *
 * 由後往前找，因為同一秒可以有兩句（實際逐字稿裡 68 秒就有兩句），由前往後找
 * 會停在已經被下一句蓋過去的那一句上。
 *
 * 兩端都夾住而不是回 -1：`secs` 比第一句早（影片有片頭）回第一句；比最後一句晚
 * （逐字稿只涵蓋前 71 秒，影片有 15:16）停在最後一句。中途把高亮整個清掉，讀者
 * 會以為對照壞了。
 */
export function cueIndexAt(cues: SubtitleCue[], secs: number): number {
  for (let index = cues.length - 1; index >= 0; index -= 1) {
    if (secs >= cues[index].start) return index
  }
  return 0
}

/**
 * 從指定秒數起播的 YouTube 連結。script 載不進來時的退路。
 *
 * 秒數**往下取整**，取的是與清單那一欄時間碼相同的數字：後端的句子起點是浮點，
 * 而這個連結的意思是「從畫面上寫的那一秒開始看」，兩邊差 0.4 秒沒有意義。
 */
export function youtubeUrlAt(id: string, secs: number): string {
  return `https://www.youtube.com/watch?v=${id}&t=${floorDisplaySeconds(secs)}s`
}
